// Card model and deterministic seeded dealing for Gin Rummy. A card id is two chars:
// rank + suit, e.g. "AS", "TD", "KH". Ace is low only. Never uses Math.random — the deal
// is reproduced on every client from the seed recorded in state.

export type CardId = string;

export const RANKS = "A23456789TJQK"; // index 0 = Ace (low), 12 = King
export const SUITS = "SHDC";

export const rankIndex = (card: CardId): number => RANKS.indexOf(card[0]);
export const suitOf = (card: CardId): string => card[1];

/** Deadwood value: Ace = 1, 2–9 = face, 10/J/Q/K = 10. */
export function deadwoodValue(card: CardId): number {
  const i = rankIndex(card);
  if (i === 0) return 1;
  return i <= 8 ? i + 1 : 10;
}

export const handDeadwoodValue = (cards: CardId[]): number =>
  cards.reduce((sum, card) => sum + deadwoodValue(card), 0);

export function fullDeck(): CardId[] {
  const deck: CardId[] = [];
  for (const suit of SUITS) for (const rank of RANKS) deck.push(rank + suit);
  return deck;
}

// mulberry32 PRNG seeded from a string hash — small, fast, and deterministic across engines.
const hashSeed = (seed: string): number => {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < seed.length; i++) {
    h = Math.imul(h ^ seed.charCodeAt(i), 16777619);
  }
  return h >>> 0;
};

function mulberry32(a: number): () => number {
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Deterministic Fisher–Yates shuffle keyed by `seed`. Does not mutate the input. */
export function seededShuffle(cards: CardId[], seed: string): CardId[] {
  const out = cards.slice();
  const rng = mulberry32(hashSeed(seed));
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

/** A fresh, non-reproducible seed for a new match (captured into state and reused thereafter). */
export function randomSeed(): string {
  const bytes = new Uint32Array(4);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(36)).join("");
}

/** Cards sorted by suit then rank — a stable ordering for display and deterministic tie-breaks. */
export function sortCards(cards: CardId[]): CardId[] {
  return cards.slice().sort((a, b) => {
    const s = SUITS.indexOf(suitOf(a)) - SUITS.indexOf(suitOf(b));
    return s !== 0 ? s : rankIndex(a) - rankIndex(b);
  });
}
